import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {DialogService, DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {Subscription} from 'rxjs';
import {AlertsConfig, ClientITProfile, Project, ProjectControlItem, User} from 'src/app/_models';
import {DropdownItem} from 'src/app/helpers/dropdownItem';
import {EvidenceCentralItem} from '../../_models/evidenceCentralItem';
import {SelectProjectControlItemComponent} from "../selectProjectControlItem/select-project-control-item.component";
import {IntegrationsService, UserService} from "../../_services";
import {InspectionProfileService} from "../../_services/inspectionProfile.service";
import {EvidenceCentralService} from "../../_services/evidenceCentral.service";
import {AccountCreatedMessageComponent} from "../accountCreatedMessage";
import {MatDialog} from "@angular/material/dialog";
import {InspectionResultsPopupComponent} from "../inspectionResultsPopup/inspectionResultsPopup.component";
import * as moment from 'moment';
import { Constants } from '../../helpers/constants';
import {Device} from "../../_models/device";
import {SelectDeviceComponent} from "../selectDevice/select-device.component";

@Component({
    standalone: false,
  selector: 'edit-edit-evidence-central',
  templateUrl: './edit-evidence-central.component.html',
  styleUrls: ['./edit-evidence-central.component.scss'],
})
export class EditEvidenceCentralComponent implements OnInit, OnDestroy {
  private categorySubscription: Subscription;

  form: FormGroup;
  projects: (Project & { itProfiles: ClientITProfile[] })[];
  integrations: any[];
  inspections: any[];
  selectedProjectControlItems: ProjectControlItem[] = [];
  selectedDevices: Device[] = [];
  isIntegration = false;
  fileName: string;
  fileContent: number[];
  evidenceCentralItem: EvidenceCentralItem;
  users: User[];
  constants = Constants;

  constructor(
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public fb: FormBuilder,
    public dialogService: DialogService,
    public integrationsService: IntegrationsService,
    public inspectionProfileService: InspectionProfileService,
    public evidenceCentralService: EvidenceCentralService,
    public userService: UserService,
  ) {
    this.projects = this.config.data.projects;
  }

  ngOnInit(): void {
    this.evidenceCentralItem = {...(this.config.data.item ?? {authDetails: {}})};
    this.selectedProjectControlItems = this.evidenceCentralItem?.projectControlItems || [];
    this.selectedDevices = this.evidenceCentralItem?.devices || [];
    this.isIntegration = this.evidenceCentralItem.integrationId > 0;
    this.userService.getAllShort().subscribe(u => this.users = u);
    this.integrationsService.getAll().subscribe(u => this.integrations = u);
    this.inspectionProfileService.getAll().subscribe(u => this.inspections = u);
    this.initializeForm();
  }

  initializeForm() {
    this.form = this.fb.group({
      name: [''],
      type: [''],
      notes: [''],
      category: ['Evidence'],
      ownerId: [null],
      inspectionProfileId: [null],
      integrationId: [null],
      settings: this.fb.group({}),
      schedule: this.fb.group({
        period: [null],
        startDateTime: [null],
        endDateTime: [null],
        daysOfMonth: [[]],
        months: [[]],
        daysOfWeek: [[]],
        hours: [null],
        minutes: [null],
      }),
    });

    let utcMoment = moment.utc();
    utcMoment.set('hour', this.evidenceCentralItem.schedule.hours)
    utcMoment.set('minute', this.evidenceCentralItem.schedule.minutes)
    utcMoment = utcMoment.local();
    this.evidenceCentralItem.schedule.hours = utcMoment.get('hours');
    this.evidenceCentralItem.schedule.minutes = utcMoment.get('minutes');
    this.form.patchValue(this.evidenceCentralItem);
  }

  onSave() {
    const formData = this.form.value;
    const formSchedule = formData.schedule;
    let matches: any[];
    let fixedTime = formData.time;
    let hours = formSchedule.hours;
    let minutes = formSchedule.minutes;
    if (matches = formSchedule.time.match(/^([\d]{1,2}):([\d]{1,2})$/)) {
      const utc = moment();
      utc.set('hour', matches[1]);
      utc.set('minute', matches[2]);
      const utcMoment = utc.utc();
      fixedTime = utcMoment.format('HH:mm');
      hours = utcMoment.get('hour');
      minutes = utcMoment.get('minute');
    }
    const scheduleConverted: AlertsConfig = {
      ...formSchedule,
      daysOfMonth: formSchedule.daysOfMonth.map((i) => i.value),
      months: formSchedule.months.map((i) => i.value),
      daysOfWeek: formSchedule.daysOfWeek.map((i) => i.value) ?? {},
      time: fixedTime,
      hours,
      minutes
    };
    const formDataValue = {
      ...this.evidenceCentralItem,
      ...formData,
      schedule: {...scheduleConverted},
      projectControlItems: this.selectedProjectControlItems,
      devices: this.selectedDevices,
      fileName: this.fileName,
      fileContent: this.fileContent
    } as EvidenceCentralItem;
    if (!this.isIntegration) {
      formDataValue.integrationId = null;
    }

    this.dialogRef.close(formDataValue);
  }

  onClose() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.categorySubscription?.unsubscribe();
  }

  selectAttachment(event) {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      this.fileContent = Array.from(new Uint8Array(<ArrayBuffer>event.target.result));
    });
    reader.readAsArrayBuffer(event.target.files[0]);
    this.fileName = event.target.files[0].name;
  }

  testAttachment(event) {
    const reader = new FileReader();
    const fileName = event.target.files[0].name;
    reader.addEventListener('load', (event) => {
      const fileContent = Array.from(new Uint8Array(<ArrayBuffer>event.target.result));
      this.evidenceCentralService.testFile({
        ...this.evidenceCentralItem,
        fileContent,
        fileName
      }).subscribe(errors => {
        this.dialogService.open(InspectionResultsPopupComponent,
          {
            width: '50%',
            contentStyle: {overflow: 'auto'},
            baseZIndex: 10010,
            maximizable: true,
            data: {
              results: errors.items
            }
          });

      });
    });
    reader.readAsArrayBuffer(event.target.files[0]);
    event.target.value = '';
  }

  addProjectControlItem() {
    const controlsDialogRef = this.dialogService.open(
      SelectProjectControlItemComponent,
      {
        width: '70%',
        contentStyle: {overflow: 'auto'},
        baseZIndex: 10010,
        maximizable: true,
        data: {},
        header: 'Select control',
      }
    );

    controlsDialogRef.onClose.subscribe((data: any) => {
      if (data) {
        data.complianceAreaControlId = data.controlItem.complianceAreaControlId;
        this.selectedProjectControlItems.push(data);
      }
    });
  }

  removeProjectControlItem(item) {
    this.selectedProjectControlItems = this.selectedProjectControlItems.filter((i) => i !== item);
  }

  addDevice() {
    const controlsDialogRef = this.dialogService.open(
      SelectDeviceComponent,
      {
        width: '70%',
        contentStyle: {overflow: 'auto'},
        baseZIndex: 10010,
        maximizable: true,
        data: {},
        header: 'Select device',
      }
    );

    controlsDialogRef.onClose.subscribe((data: any) => {
      if (data) {
        this.selectedDevices.push(data);
      }
    });
  }

  removeDevice(item) {
    this.selectedDevices = this.selectedDevices.filter((i) => i !== item);
  }
}
